// The app's per-user data lifecycle: auth state, profile (+ derived theme/goals/
// tour), and the 4 shared collections (clients/appointments/payments/attendance)
// — plus the write paths that keep Firestore, the LS cache, and this state in
// sync. Everything here is scoped to "the signed-in user's data"; navigation/UI
// state (page, showProfile, showSearch, the back-button stack, …) stays in App().
//
// `page` is the one nav concern this needs to know about — the aggregate-screen
// (dashboard/reports) background-sync trigger reads it.
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { getRedirectResult, signOut, onAuthStateChanged } from "firebase/auth";
import { doc, getDoc, setDoc, deleteDoc } from "firebase/firestore";
import { auth, db, track, userMeta, LS } from "../lib/firebase";
import { clampGoal, tzOffsetEast } from "../lib/format";
import { COLOR_PALETTES, buildTheme } from "../lib/theme";
import { isOnboarded, markOnboarded, syncCol } from "../lib/sync";
import { isNativeApp } from "../native";

export function useUserData(page) {
  const [user,setUser]=useState(null);
  const [authLoading,setAuthLoading]=useState(true);
  const [authError,setAuthError]=useState("");
  const [colorKey,setColorKey]=useState("teal");
  const [modeKey,setModeKey]=useState("light");
  const [profile,setProfileRaw]=useState({name:"",email:"",services:[]});
  const [clients,setClientsRaw]=useState([]);
  const [appointments,setAppointments]=useState([]);
  const [payments,setPayments]=useState([]);
  // State + LS cache in one step, for the Payments screen's edits.
  const persistPayments=useCallback((updater)=>setPayments(prev=>{
    const next=typeof updater==="function"?updater(prev):updater;
    const uid=auth.currentUser?.uid;
    if(uid)LS.set(`ufcm_payments_${uid}`,next);
    return next;
  }),[]);
  const [attendanceRecords,setAttendanceRecords]=useState([]);
  const [dataLoading,setDataLoading]=useState(false);
  // True once we actually KNOW whether this user needs onboarding — either a
  // Firestore profile read settled this session, or a cached complete profile /
  // the onboarded flag told us. The "Quick Setup" screen only shows when this is
  // true, so a slow first sync shows the skeleton, never a stray signup form.
  const [profileChecked,setProfileChecked]=useState(false);
  const [monthlyGoal,setMonthlyGoalRaw]=useState(0);
  const [yearlyGoal,setYearlyGoalRaw]=useState(0);
  const [tourDone,setTourDoneRaw]=useState(false);
  const [showTour,setShowTour]=useState(false);
  const theme=useMemo(()=>buildTheme(colorKey,modeKey),[colorKey,modeKey]);
  const isDark=theme.dark;

  // Push a profile object into UI state (profile + derived theme/goals/tour).
  const applyProfile=useCallback((p)=>{
    if(!p)return;
    setProfileRaw(p);
    if(p.colorKey){
      // Current scheme (colorKey + modeKey). Runs on EVERY background sync, so it
      // must not fall through to a legacy `themeKey` branch and revert the
      // user's chosen palette back to the signup default.
      setColorKey(p.colorKey);
      if(p.modeKey)setModeKey(p.modeKey);
    } else if(p.themeKey==="dark"){
      setModeKey("dark");setColorKey("teal");
    } else if(p.themeKey&&COLOR_PALETTES[p.themeKey]){
      // Legacy: themeKey held the palette name (pre-v3.5.0).
      setColorKey(p.themeKey);setModeKey("light");
    } else if(p.modeKey){
      setModeKey(p.modeKey);
    }
    // `!= null` not truthiness — a cross-device reset to 0 must propagate.
    if(p.monthlyGoal!=null)setMonthlyGoalRaw(clampGoal(p.monthlyGoal));
    if(p.yearlyGoal!=null)setYearlyGoalRaw(clampGoal(p.yearlyGoal));
    if(p.tourDone)setTourDoneRaw(true);
  },[]);

  // ── BACKGROUND SYNC ───────────────────────────────────────────────────────
  // Refresh profile + every shared collection from Firestore, and the LS cache
  // with them. Safe to call any time: offline it resolves from Firestore's own
  // cache or fails quietly, leaving cached data in place. Runs on login, on
  // regaining connectivity, on app resume, on landing on an aggregate screen,
  // and on a light interval — data keeps arriving without an app restart.
  const lastSyncRef=useRef(0);
  const syncPausedRef=useRef(0); // >0 while a form/modal is open — see EditGuard
  const editGuard=useMemo(()=>({
    pause:()=>{syncPausedRef.current++;},
    resume:()=>{syncPausedRef.current=Math.max(0,syncPausedRef.current-1);},
  }),[]);
  const syncFromFirestore=useCallback(async(uid,{firstRun=false,force=false}={})=>{
    if(!uid)return;
    if(!force){
      if(syncPausedRef.current>0)return;              // a form is open — don't clobber edits
      if(Date.now()-lastSyncRef.current<45000)return; // throttle bursty triggers
    }
    lastSyncRef.current=Date.now();
    const syncProfile=async()=>{
      try{
        const snap=await getDoc(userMeta(uid));
        if(snap.exists()){
          const p=snap.data();
          applyProfile(p);
          LS.set(`ufcm_profile_${uid}`,p);
          if(p.profileComplete)markOnboarded(uid);
          // Tour only for a genuinely new account — not a returning user whose
          // cache was evicted (firstRun is just "no local profile cache").
          if(firstRun&&!p.tourDone&&!p.profileComplete&&!isOnboarded(uid))
            setTimeout(()=>setShowTour(true),800);
        }else if(!isOnboarded(uid)&&!LS.get(`ufcm_profile_${uid}`)?.profileComplete){
          // Genuinely new user — seed Firestore only. Never cache profileComplete:false;
          // a transient empty read must not strand a real user on Quick Setup.
          const seed={name:auth.currentUser?.displayName||"",email:auth.currentUser?.email||"",services:[],colorKey:"teal",modeKey:"light",profileComplete:false};
          applyProfile(seed);
          setDoc(userMeta(uid),seed).catch(e=>console.error("Seed error:",e));
        }
        setProfileChecked(true);
      }catch(e){/* offline / transient — keep whatever is on screen */}
      // Always clear loading — offline with no cache shows the (empty) app shell,
      // not an endless skeleton. needsQuickSetup stays false until profileChecked.
      setDataLoading(false);
    };
    await Promise.all([
      syncProfile(),
      syncCol(uid,"clients",setClientsRaw,false),
      syncCol(uid,"appointments",setAppointments,false),
      syncCol(uid,"payments",setPayments,false),
      syncCol(uid,"attendance",setAttendanceRecords,false),
    ]);
    LS.set(`ufcm_clients_last_sync_${uid}`,Date.now());
  },[applyProfile]);

  // ── SAFARI REDIRECT RESULT ────────────────────────────────────────────────
  useEffect(()=>{
    getRedirectResult(auth).catch(e=>{
      const benign=["auth/no-auth-event","auth/network-request-failed","auth/timeout"];
      if(e?.code&&!benign.includes(e.code))setAuthError("Sign-in didn't complete. Please try again.");
    });
  },[]);

  // ── AUTH LISTENER ──────────────────────────────────────────────────────────
  useEffect(()=>{
    track('app_open',{platform:window.Capacitor?.isNativePlatform?.()?.toString()||'web'});
    // Fresh install detection: localStorage is wiped on Android uninstall.
    // Firebase Keystore credentials survive, so we force sign-out on first launch.
    if(!LS.get("hasLaunchedBefore")){
      signOut(auth).catch(()=>{});
      LS.set("hasLaunchedBefore","1");
    }
    const unsub=onAuthStateChanged(auth,(u)=>{
      setUser(u);
      setAuthLoading(false);
      if(!u){setProfileChecked(false);setDataLoading(false);return;}
      setAuthError("");

      // ── STEP 1: hydrate instantly from the localStorage cache ──────────────
      // Lists hydrate regardless of whether the profile cache survived, so the
      // Dashboard ledger shows real numbers offline, not zeros.
      const cachedProfile=LS.get(`ufcm_profile_${u.uid}`);
      if(cachedProfile)applyProfile(cachedProfile);
      [["clients",setClientsRaw],["appointments",setAppointments],
       ["payments",setPayments],["attendance",setAttendanceRecords]].forEach(([c,set])=>{
        const v=LS.get(`ufcm_${c}_${u.uid}`);
        if(Array.isArray(v))set(v);
      });
      // Known user → show the app now, sync fills in behind. Otherwise show the
      // skeleton until the first sync settles (never the signup form — that's
      // gated on profileChecked).
      const known=cachedProfile?.profileComplete||isOnboarded(u.uid);
      if(known)setProfileChecked(true);
      setDataLoading(!known);

      // ── STEP 2: pull fresh data (and keep pulling — see BACKGROUND SYNC) ───
      syncFromFirestore(u.uid,{firstRun:!cachedProfile,force:true});

      // Refresh the notification timezone offset every launch so a DST change or
      // a move is picked up without waiting for an FCM token refresh. merge:true
      // with only this field — it never creates an fcmToken.
      setDoc(doc(db,"users",u.uid,"settings","notifications"),
        {timezoneOffset:tzOffsetEast()},{merge:true}).catch(()=>{});
    });
    return unsub;
  },[applyProfile,syncFromFirestore]);

  // ── BACKGROUND SYNC TRIGGERS ──────────────────────────────────────────────
  // Event-driven only (reconnect, app foreground, aggregate-screen nav). No
  // polling interval — a full collection re-read every 90s on mobile data is
  // not worth it; the events above cover "the user is looking at fresh data".
  useEffect(()=>{
    if(!user?.uid)return;
    const uid=user.uid;
    const sync=()=>syncFromFirestore(uid);
    const onVisible=()=>{if(document.visibilityState==="visible")sync();};
    window.addEventListener("online",sync);
    document.addEventListener("visibilitychange",onVisible);
    let capListener=null,cancelled=false;
    if(isNativeApp()){
      import("@capacitor/app").then(({App:CapApp})=>{
        if(cancelled)return; // effect already torn down — don't register
        CapApp.addListener("resume",sync).then(l=>{
          if(cancelled)l.remove(); else capListener=l;
        });
      }).catch(()=>{});
    }
    return()=>{
      cancelled=true;
      window.removeEventListener("online",sync);
      document.removeEventListener("visibilitychange",onVisible);
      if(capListener)capListener.remove();
    };
  },[user?.uid,syncFromFirestore]);

  // Landing on an aggregate screen (throttled) — a lightweight refresh so a user
  // who edited data on another device sees it without backgrounding/reopening.
  useEffect(()=>{
    if(user?.uid&&(page==="dashboard"||page==="reports"))syncFromFirestore(user.uid);
  },[page,user?.uid,syncFromFirestore]);

  // ── PROFILE SAVE ───────────────────────────────────────────────────────────
  const setProfile=async(p)=>{
    setProfileRaw(p);
    if(user){
      LS.set(`ufcm_profile_${user.uid}`,p); // cache locally
      await setDoc(userMeta(user.uid),p,{merge:true});
    }
  };

  // ── CLIENTS CRUD ───────────────────────────────────────────────────────────
  const setClients=async(fn)=>{
    setClientsRaw(prev=>{
      const next=typeof fn==="function"?fn(prev):fn;
      if(user){
        LS.set(`ufcm_clients_${user.uid}`,next); // cache locally
        const before=new Map(prev.map(c=>[c.id,c]));
        next.forEach(c=>{
          // only write docs that actually changed — most callers touch one client,
          // and marking one attendance shouldn't rewrite the whole collection
          if(before.get(c.id)===c)return;
          const {id,...data}=c;
          setDoc(doc(db,"users",user.uid,"clients",id),data).catch(e=>console.error("Client save failed:",e));
        });
        const after=new Set(next.map(c=>c.id));
        prev.forEach(c=>{
          if(!after.has(c.id)){
            deleteDoc(doc(db,"users",user.uid,"clients",c.id)).catch(e=>console.error("Client delete failed:",e));
          }
        });
      }
      return next;
    });
  };

  // One write path for renew / extend: optimistic local state FIRST (survives
  // offline / app-kill), THEN a fire-and-forget field-patch write ({merge:true}).
  // Never awaits a Firestore promise before a setState — setDoc doesn't resolve
  // while offline. `patchOrFn` is a field-patch object or (client) => patch,
  // resolved here against current `clients`; `notify` is an optional (msg,level)
  // toast for a late error.
  const applyClientAndPayment=useCallback((clientId,patchOrFn,payment,notify)=>{
    const u=auth.currentUser?.uid;
    const cur=clients.find(c=>c.id===clientId);
    const patch=typeof patchOrFn==="function"?(cur?patchOrFn(cur):null):patchOrFn;
    if(!patch)return;
    setClientsRaw(prev=>{
      const next=prev.map(c=>c.id===clientId?{...c,...patch}:c);
      if(u)LS.set(`ufcm_clients_${u}`,next);
      return next;
    });
    if(payment)persistPayments(prev=>[payment,...prev]);
    if(!u)return;
    setDoc(doc(db,"users",u,"clients",clientId),patch,{merge:true})
      .catch(e=>{console.error("client save (renew/extend):",e);if(notify)notify("Couldn't sync the change — it'll retry when you're online.","warning");});
    if(payment){
      const {id:pid,...pdata}=payment;
      setDoc(doc(db,"users",u,"payments",pid),pdata)
        .catch(e=>{console.error("payment save (renew/extend):",e);if(notify)notify("Payment didn't sync — it'll retry when you're online.","warning");});
    }
  },[clients,persistPayments]);

  // ── THEME / GOALS / TOUR SAVE ─────────────────────────────────────────────
  const saveColorKey=async(k)=>{
    setColorKey(k);
    if(user)await setDoc(userMeta(user.uid),{colorKey:k},{merge:true});
  };
  const saveModeKey=async(k)=>{
    setModeKey(k);
    if(user)await setDoc(userMeta(user.uid),{modeKey:k},{merge:true});
  };
  const setMonthlyGoal=async(v)=>{
    const g=clampGoal(v);
    setMonthlyGoalRaw(g);
    if(user)await setDoc(userMeta(user.uid),{monthlyGoal:g},{merge:true});
  };
  const setYearlyGoal=async(v)=>{
    const g=clampGoal(v);
    setYearlyGoalRaw(g);
    if(user)await setDoc(userMeta(user.uid),{yearlyGoal:g},{merge:true});
  };
  const completeTour=async()=>{
    setTourDoneRaw(true);setShowTour(false);
    if(user)await setDoc(userMeta(user.uid),{tourDone:true},{merge:true});
  };

  return {
    user,setUser,authLoading,authError,setAuthError,
    profile,setProfileRaw,setProfile,
    clients,setClientsRaw,setClients,applyClientAndPayment,
    appointments,setAppointments,
    payments,setPayments,persistPayments,
    attendanceRecords,setAttendanceRecords,
    dataLoading,profileChecked,
    colorKey,modeKey,saveColorKey,saveModeKey,theme,isDark,
    monthlyGoal,setMonthlyGoal,yearlyGoal,setYearlyGoal,
    tourDone,setTourDoneRaw,showTour,setShowTour,completeTour,
    syncFromFirestore,editGuard,
  };
}
